import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { desktopNotificationApi } from '@/api/desktopNotificationApi';
import type {
  DesktopNotificationCreateRequestDto,
  DesktopNotificationType,
  DesktopNotificationUpdateRequestDto,
} from '@/api/types/desktopNotification.types';

export const desktopNotificationKeys = {
  all: ['desktopNotifications'] as const,
  list: (enabled?: boolean) => ['desktopNotifications', 'list', { enabled }] as const,
  detail: (id: number) => ['desktopNotifications', 'detail', id] as const,
  placeholders: (type?: DesktopNotificationType | null) =>
    ['desktopNotifications', 'placeholders', { type: type ?? null }] as const,
  audienceStaffOptions: (type?: DesktopNotificationType | null) =>
    ['desktopNotifications', 'audienceStaffOptions', { type: type ?? null }] as const,
};

export function useDesktopNotifications(enabled?: boolean) {
  return useQuery({
    queryKey: desktopNotificationKeys.list(enabled),
    queryFn: () => desktopNotificationApi.getAll(enabled),
  });
}

export function useDesktopNotification(id: number) {
  return useQuery({
    queryKey: desktopNotificationKeys.detail(id),
    queryFn: () => desktopNotificationApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateDesktopNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: DesktopNotificationCreateRequestDto) =>
      desktopNotificationApi.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: desktopNotificationKeys.all });
    },
  });
}

export function useUpdateDesktopNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: DesktopNotificationUpdateRequestDto }) =>
      desktopNotificationApi.update(id, request),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: desktopNotificationKeys.all });
      queryClient.invalidateQueries({ queryKey: desktopNotificationKeys.detail(id) });
    },
    onError: (error, { id }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({ queryKey: desktopNotificationKeys.all });
        queryClient.invalidateQueries({ queryKey: desktopNotificationKeys.detail(id) });
      }
    },
  });
}

export function useDeleteDesktopNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rowVersion }: { id: number; rowVersion: string }) =>
      desktopNotificationApi.delete(id, rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: desktopNotificationKeys.all });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({ queryKey: desktopNotificationKeys.all });
      }
    },
  });
}

export function useDesktopNotificationPlaceholders(type?: DesktopNotificationType | null) {
  return useQuery({
    queryKey: desktopNotificationKeys.placeholders(type),
    queryFn: () => desktopNotificationApi.getPlaceholders(type),
    staleTime: Infinity,
  });
}

export function useAudienceStaffOptions(type?: DesktopNotificationType | null) {
  return useQuery({
    queryKey: desktopNotificationKeys.audienceStaffOptions(type),
    queryFn: () => desktopNotificationApi.getAudienceStaffOptions(type),
    staleTime: 5 * 60 * 1000,
  });
}
